/** How open a license is, at a glance. Three tags rather than seven, because
 * the point is to sort the list, not to re-state the code. */
export type Openness = 'Most open' | 'Sort of open' | 'Most restrictive';

/** Openest to tightest, plus Custom. Opaque ramp shades, not alpha over the
 * card — these sit on a tinted background once a card is selected. */
export const OPENNESS_TINT: Record<Openness | 'Custom', string> = {
  'Most open': 'bg-green-100 text-green-800',
  'Sort of open': 'bg-amber-100 text-amber-700',
  'Most restrictive': 'bg-red-100 text-red-700',
  Custom: 'bg-blue-200 text-blue-600',
};

/** What a license permits, in the order the comparison table lists them. */
export interface Caps {
  make: boolean;
  modify: boolean;
  shareOriginal: boolean;
  shareModified: boolean;
  sellItems: boolean;
  commercial: boolean;
  sellDigital: boolean;
  shareDigital: boolean;
  attribution: boolean;
}

export interface LicenseInfo {
  code: string;
  name: string;
  openness: Openness;
  desc: string;
  caps: Caps;
}

const caps = (bits: string): Caps => {
  const [
    make,
    modify,
    shareOriginal,
    shareModified,
    sellItems,
    commercial,
    sellDigital,
    shareDigital,
    attribution,
  ] = [...bits].map((c) => c === '1');
  return {
    make,
    modify,
    shareOriginal,
    shareModified,
    sellItems,
    commercial,
    sellDigital,
    shareDigital,
    attribution,
  };
};

/**
 * The seven Creative Commons licenses the backend stores, openest first.
 *
 * `modify` is TRUE under ND: the license restricts DISTRIBUTING a derivative,
 * not cutting a changed version for yourself.
 */
export const LICENSES: LicenseInfo[] = [
  {
    code: 'CC0',
    name: 'Public Domain',
    openness: 'Most open',
    desc: 'No rights reserved. Anyone can use, change, even sell your files — no credit required.',
    caps: caps('111111110'),
  },
  {
    code: 'CC BY',
    name: 'Attribution',
    openness: 'Most open',
    desc: 'Others may use, change and sell your work, as long as they credit you as the maker.',
    caps: caps('111111111'),
  },
  {
    code: 'CC BY-SA',
    name: 'Attribution · ShareAlike',
    openness: 'Most open',
    desc: 'Same as Attribution, but anything made from it must be shared under this same license.',
    caps: caps('111111111'),
  },
  {
    code: 'CC BY-ND',
    name: 'Attribution · NoDerivatives',
    openness: 'Sort of open',
    desc: "Others may share and even sell it with credit, but they can't modify your files.",
    caps: caps('111011111'),
  },
  {
    code: 'CC BY-NC',
    name: 'Attribution · NonCommercial',
    openness: 'Sort of open',
    desc: 'Others may use and change it with credit, but not for commercial (paid) use.',
    caps: caps('111100011'),
  },
  {
    code: 'CC BY-NC-SA',
    name: 'Attribution · NonCommercial · ShareAlike',
    openness: 'Sort of open',
    desc: 'Noncommercial use with credit, and any remix must keep this same license.',
    caps: caps('111100011'),
  },
  {
    code: 'CC BY-NC-ND',
    name: 'Attribution · NonCommercial · NoDerivatives',
    openness: 'Most restrictive',
    desc: 'Most restrictive: share with credit only — no changes, no commercial use.',
    caps: caps('111000011'),
  },
];

export const CAPABILITY_ROWS: Array<{ key: keyof Caps; label: string }> = [
  { key: 'make', label: 'Make the project for themselves' },
  { key: 'modify', label: 'Modify the files' },
  { key: 'shareOriginal', label: 'Share original files' },
  { key: 'shareModified', label: 'Share modified files' },
  { key: 'sellItems', label: 'Sell finished physical items' },
  { key: 'commercial', label: 'Use commercially' },
  { key: 'sellDigital', label: 'Sell digital files' },
  { key: 'shareDigital', label: 'Share digital files for free' },
  { key: 'attribution', label: 'Attribution required' },
];
